import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FullLogo } from '@/components/brand/Marks';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowRight, Building2, CheckCircle, Loader2, Lock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A' };

const INVALID_COPY = {
  not_found: 'This invite link is not valid. Check the link in your email, or ask your MGA to send a new one.',
  expired: 'This invite link has expired. Ask your MGA to resend the invitation.',
  accepted: 'This invite has already been used. Sign in with your email and password instead.',
};

export default function JoinPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/signup/invite/${token}`)
      .then(r => { setInvite(r.data); if (r.data.valid) setName(r.data.broker_name || ''); })
      .catch(() => setInvite({ valid: false, reason: 'not_found' }));
  }, [token]);

  const activate = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/signup/invite/${token}/activate`,
        { password, name, phone }, { withCredentials: true });
      if (data.token) sessionStorage.setItem('aira_session', data.token);
      window.location.href = '/broker';
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Activation failed — please try again.');
      setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F5F7FA' }} data-testid="join-page">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><FullLogo size="md" /></div>
        <div className="bg-white p-8" style={{ border: '1px solid rgba(136,150,168,0.25)' }}>{children}</div>
        <p className="text-center text-[11px] mt-4" style={{ color: C.slate }}>frAIme by underAIter.com — AI-native underwriting</p>
      </div>
    </div>
  );

  if (!invite) return <Shell><div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: C.blue }} /></div></Shell>;

  if (!invite.valid) return (
    <Shell>
      <div className="text-center py-4" data-testid="join-invalid">
        <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: C.red }} />
        <p className="text-sm" style={{ color: C.navy }}>{INVALID_COPY[invite.reason] || INVALID_COPY.not_found}</p>
        <a href="/login?staff=1" className="inline-block mt-5 text-sm font-semibold hover:underline" style={{ color: C.blue }}>Go to sign in →</a>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Broker panel invitation</p>
      <h1 className="text-xl font-semibold mb-1" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>
        Welcome to {invite.mga_name}
      </h1>
      <p className="text-sm mb-5" style={{ color: C.slate }}>Two quick steps and you can quote and bind in minutes.</p>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {[['1', 'You & your brokerage'], ['2', 'Secure your account']].map(([n, label], i) => (
          <React.Fragment key={n}>
            {i > 0 && <div className="flex-1 h-px" style={{ background: 'rgba(136,150,168,0.3)' }} />}
            <div className="flex items-center gap-1.5">
              <span className="w-5 h-5 flex items-center justify-center text-[10px] font-bold"
                style={{ background: step > i ? C.blue : '#fff', color: step > i ? '#fff' : C.slate, border: `1px solid ${step > i ? C.blue : 'rgba(136,150,168,0.4)'}` }}>
                {step > i + 1 ? '✓' : n}
              </span>
              <span className="text-[11px] font-medium" style={{ color: step > i ? C.navy : C.slate }}>{label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4" data-testid="join-step-1">
          <div className="p-3 flex items-start gap-2.5" style={{ background: 'rgba(0,180,216,0.06)', border: '1px solid rgba(0,180,216,0.25)' }}>
            <Building2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.teal }} />
            <div>
              <p className="text-xs font-semibold" style={{ color: C.navy }} data-testid="join-brokerage-name">{invite.brokerage_name || 'Independent broker'}</p>
              <p className="text-[11px]" style={{ color: C.slate }}>You'll be aligned under this brokerage on the {invite.mga_name} panel.</p>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>Your name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-sm" required data-testid="join-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>Phone (optional)</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="04xx xxx xxx" className="mt-1 rounded-sm" data-testid="join-phone-input" />
          </div>
          <p className="text-[11px]" style={{ color: C.slate }}>Signing up as <b>{invite.broker_email}</b></p>
          <Button onClick={() => { if (!name.trim()) { setError('Please enter your name.'); return; } setError(''); setStep(2); }}
            className="w-full rounded-sm font-semibold text-white" style={{ background: C.blue, height: 44 }} data-testid="join-continue-btn">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={activate} className="space-y-4" data-testid="join-step-2">
          <div>
            <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>Set your password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters"
              className="mt-1 rounded-sm" required data-testid="join-password-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>Confirm password</Label>
            <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              className="mt-1 rounded-sm" required data-testid="join-confirm-input" />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => { setError(''); setStep(1); }} className="rounded-sm" data-testid="join-back-btn">Back</Button>
            <Button type="submit" disabled={busy} className="flex-1 rounded-sm font-semibold text-white" style={{ background: C.green, height: 44 }}
              data-testid="join-activate-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              {busy ? 'Activating…' : 'Activate & sign in'}
            </Button>
          </div>
        </form>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm p-3 mt-4" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }} data-testid="join-error">
          <AlertCircle className="w-4 h-4 shrink-0" /><span>{error}</span>
        </div>
      )}
    </Shell>
  );
}
